// card de localizacao + cerca virtual (rev 085553)
import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api.ts";
import { relativeTime } from "../lib/hooks.ts";
import { Card, Spinner } from "./ui.tsx";

interface LocPoint {
  lat: number;
  lon: number;
  accuracy: number;
  source: string;
  ip: string;
  ssid: string;
  city: string;
  region: string;
  at: string;
  distance?: number;
}

interface LocBase {
  lat: number;
  lon: number;
  label: string;
}

interface LocManual {
  lat: number;
  lon: number;
  at: string;
  label: string;
  city?: string;
  region?: string;
}

interface LocFence {
  enabled: boolean;
  radiusM: number;
  state: string;
  alertAt: string;
}

interface LocResponse {
  last: LocPoint | null;
  history: LocPoint[];
  requestedAt: string;
  base: LocBase | null;
  manual: LocManual | null;
  fence: LocFence;
}

function fmtAccuracy(m: number): string {
  if (!m || m <= 0) return "nao informada";
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
}

function fmtDistance(m: number): string {
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function sourceLabel(s: string): string {
  if (s === "windows") return "Windows (Wi-Fi/GPS)";
  if (s === "ip") return "por IP (cidade)";
  if (s === "manual") return "ajustado manualmente";
  return s || "desconhecida";
}

function hollowIcon(): L.DivIcon {
  return L.divIcon({
    className: "",
    html:
      `<div style="width:14px;height:14px;border-radius:50%;background:rgba(255,255,255,0.10);` +
      `border:2px solid #5b9bff;opacity:.85"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function dotIcon(color: string, ring: string): L.DivIcon {
  return L.divIcon({
    className: "",
    html:
      `<div style="width:20px;height:20px;border-radius:50%;background:${color};` +
      `border:3px solid #fff;box-shadow:0 0 0 3px ${ring};cursor:grab"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

export function DeviceLocationCard({
  deviceUid,
  canEdit = false,
}: {
  deviceUid: string;
  canEdit?: boolean;
}) {
  const [data, setData] = useState<LocResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [radius, setRadius] = useState(500);
  const [label, setLabel] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [pendingBase, setPendingBase] = useState<{ lat: number; lon: number } | null>(null);
  const [pendingPos, setPendingPos] = useState<{ lat: number; lon: number } | null>(null);
  // trava por referencia: uma resposta atrasada do servidor nao pode
  // sobrescrever o que o usuario esta digitando/arrastando
  const dirtyRef = useRef(false);
  const markDirty = () => { dirtyRef.current = true; };

  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapObj = useRef<L.Map | null>(null);
  const devMarker = useRef<L.Marker | null>(null);
  const estMarker = useRef<L.Marker | null>(null);
  const accCircle = useRef<L.Circle | null>(null);
  const baseMarker = useRef<L.Marker | null>(null);
  const baseCircle = useRef<L.Circle | null>(null);
  const fitKey = useRef("");

  const load = useCallback(async () => {
    try {
      const r = await api<LocResponse>(`/api/v3/devices/${deviceUid}/location`);
      setData(r);
      setError(null);
      if (!dirtyRef.current) {
        setRadius(r.fence?.radiusM ?? 500);
        setEnabled(Boolean(r.fence?.enabled));
        setLabel(r.base?.label ?? "");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar a localizacao");
    } finally {
      setLoading(false);
    }
  }, [deviceUid]);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 20000);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    const req = data?.requestedAt;
    if (!req) return;
    const t = new Date(req).getTime();
    if (!t || Date.now() - t > 5 * 60 * 1000) return;
    const id = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(id);
  }, [data?.requestedAt, load]);

  useEffect(() => {
    if (!mapEl.current || mapObj.current) return;
    const m = L.map(mapEl.current, { zoomControl: true }).setView([-15.7939, -47.8828], 4);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
      maxNativeZoom: 18,
      maxZoom: 20,
      attribution: "Esri, Maxar, Earthstar Geographics",
    }).addTo(m);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png", {
      maxNativeZoom: 18,
      maxZoom: 20,
      subdomains: "abcd",
      attribution: "&copy; OpenStreetMap &copy; CARTO",
    }).addTo(m);
    mapObj.current = m;
    const t = window.setTimeout(() => m.invalidateSize(), 300);
    return () => {
      window.clearTimeout(t);
      m.remove();
      mapObj.current = null;
      devMarker.current = null;
      estMarker.current = null;
      accCircle.current = null;
      baseMarker.current = null;
      baseCircle.current = null;
    };
  }, []);

  // Circulo de incerteza + anel da estimativa crua do Windows
  useEffect(() => {
    const m = mapObj.current;
    const p = data?.last;
    if (!m || !p) return;
    const ll: L.LatLngExpression = [p.lat, p.lon];
    const r = Math.max(30, p.accuracy || 300);
    // circulo de incerteza so faz sentido para Wi-Fi/GPS; IP (cidade) vira um
    // circulo de dezenas de km que so atrapalha o mapa
    const showCircle = p.source !== "ip" && r > 0 && r < 5000;
    if (!showCircle) {
      if (accCircle.current) { m.removeLayer(accCircle.current); accCircle.current = null; }
    } else if (!accCircle.current) {
      accCircle.current = L.circle(ll, {
        radius: r, weight: 1, color: "#5b9bff", fillColor: "#5b9bff", fillOpacity: 0.10,
      }).addTo(m);
    } else {
      accCircle.current.setLatLng(ll);
      accCircle.current.setRadius(r);
    }
    if (!estMarker.current) {
      estMarker.current = L.marker(ll, { icon: hollowIcon(), title: "Estimativa do Windows" }).addTo(m);
    } else {
      estMarker.current.setLatLng(ll);
    }

  }, [data?.last]);

  // Enquadramento: usa a posicao efetiva (corrigida) + base da cerca.
  // Nunca enquadra a leitura crua de IP, que pode estar a centenas de km.
  useEffect(() => {
    const m = mapObj.current;
    if (!m) return;
    const pts: L.LatLngExpression[] = [];
    const eff = (data?.manual ? { lat: data.manual.lat, lon: data.manual.lon } : null)
      ?? (data?.last ? { lat: data.last.lat, lon: data.last.lon } : null);
    if (eff) pts.push([eff.lat, eff.lon]);
    if (data?.base) pts.push([data.base.lat, data.base.lon]);
    if (pts.length === 0) return;
    const key = pts.map((p) => {
      const a = p as [number, number];
      return `${a[0].toFixed(4)},${a[1].toFixed(4)}`;
    }).join("|");
    if (key === fitKey.current) return;
    fitKey.current = key;
    if (pts.length === 1) {
      m.setView(pts[0] as [number, number], 16);
    } else {
      m.fitBounds(L.latLngBounds(pts as [number, number][]), { padding: [50, 50], maxZoom: 17 });
    }
    window.setTimeout(() => m.invalidateSize(), 80);
  }, [data?.manual, data?.base, data?.last]);
  // Bolinha azul: posicao corrigida (arrastavel e persistida)
  useEffect(() => {
    const m = mapObj.current;
    if (!m) return;
    const pos = pendingPos ?? (data?.manual ? { lat: data.manual.lat, lon: data.manual.lon } : null)
      ?? (data?.last ? { lat: data.last.lat, lon: data.last.lon } : null);
    if (!pos) {
      if (devMarker.current) { m.removeLayer(devMarker.current); devMarker.current = null; }
      return;
    }
    const ll: L.LatLngExpression = [pos.lat, pos.lon];
    if (!devMarker.current) {
      devMarker.current = L.marker(ll, {
        icon: dotIcon("#0b63f6", "rgba(11,99,246,0.35)"),
        draggable: canEdit,
        autoPan: true,
        title: "Arraste para corrigir a posicao",
      }).addTo(m);
      if (canEdit) {
        devMarker.current.on("dragend", (e) => {
          const c = (e.target as L.Marker).getLatLng();
          setPendingPos({ lat: c.lat, lon: c.lng });
          markDirty();
          setOk(null);
        });
      }
    } else {
      devMarker.current.setLatLng(ll);
    }
  }, [pendingPos, data?.manual, data?.last, canEdit]);

  // Base da cerca (verde, arrastavel) + circulo
  useEffect(() => {
    const m = mapObj.current;
    if (!m) return;
    const base = pendingBase ?? (data?.base ? { lat: data.base.lat, lon: data.base.lon } : null);
    if (!base) {
      if (baseMarker.current) { m.removeLayer(baseMarker.current); baseMarker.current = null; }
      if (baseCircle.current) { m.removeLayer(baseCircle.current); baseCircle.current = null; }
      return;
    }
    const ll: L.LatLngExpression = [base.lat, base.lon];
    if (!baseMarker.current) {
      baseMarker.current = L.marker(ll, {
        icon: dotIcon("#16a34a", "rgba(22,163,74,0.35)"),
        draggable: canEdit,
        autoPan: true,
        title: "Arraste para ajustar a base da cerca",
      }).addTo(m);
      if (canEdit) {
        baseMarker.current.on("dragend", (e) => {
          const c = (e.target as L.Marker).getLatLng();
          setPendingBase({ lat: c.lat, lon: c.lng });
          markDirty();
          setOk(null);
        });
      }
    } else {
      baseMarker.current.setLatLng(ll);
    }
    const rr = Math.max(50, Number(radius) || 500);
    if (!baseCircle.current) {
      baseCircle.current = L.circle(ll, {
        radius: rr, weight: 2, dashArray: "6 6", color: "#16a34a", fillColor: "#16a34a", fillOpacity: 0.06,
      }).addTo(m);
    } else {
      baseCircle.current.setLatLng(ll);
      baseCircle.current.setRadius(rr);
    }
  }, [pendingBase, data?.base, radius, canEdit]);

  async function saveFence() {
    setSaving(true);
    setError(null);
    setOk(null);
    try {
      const body: Record<string, unknown> = {
        radiusM: Math.max(50, Number(radius) || 500),
        enabled,
        label: label.trim(),
      };
      if (pendingBase) {
        body.lat = pendingBase.lat;
        body.lon = pendingBase.lon;
      }
      if (pendingPos) {
        body.manualLat = pendingPos.lat;
        body.manualLon = pendingPos.lon;
      }
      await api(`/api/v3/devices/${deviceUid}/geofence`, { method: "POST", body });
      dirtyRef.current = false;
      setPendingBase(null);
      setPendingPos(null);
      setOk("Posicao e cerca salvas.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function clearManual() {
    setSaving(true);
    setError(null);
    setOk(null);
    try {
      await api(`/api/v3/devices/${deviceUid}/geofence`, { method: "POST", body: { manualClear: true } });
      setPendingPos(null);
      dirtyRef.current = false;
      setOk("Posicao manual removida (voltou para a estimativa do Windows).");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao limpar");
    } finally {
      setSaving(false);
    }
  }

  async function requestNow() {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/v3/devices/${deviceUid}/location`, { method: "POST" });
      await load();
      window.setTimeout(() => void load(), 8000);
      window.setTimeout(() => setBusy(false), 20000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao solicitar a localizacao");
      setBusy(false);
    }
  }

  const last = data?.last ?? null;
  const base = data?.base ?? null;
  const manual = data?.manual ?? null;
  // posicao efetiva: a corrigida manualmente tem prioridade sobre a leitura do Windows
  const eff = manual ?? (last ? { lat: last.lat, lon: last.lon, at: last.at } : null);
  const fence = data?.fence;
  const pending = Boolean(
    data?.requestedAt && Date.now() - new Date(data.requestedAt).getTime() < 5 * 60 * 1000,
  );
  const distance = last?.distance && last.distance > 0 ? last.distance : null;
  // leitura do Windows sem variacao (mesmos roteadores) e sem alerta de cerca
  const stale = (() => {
    const h = data?.history ?? [];
    if (h.length < 4 || !last) return 0;
    const first = h[Math.min(h.length - 1, 4)];
    if (!first) return 0;
    const moved = Math.abs(last.lat - first.lat) + Math.abs(last.lon - first.lon);
    return moved < 0.0005 ? h.length : 0;
  })();
  const outside = fence?.enabled && fence.state === "outside";

  return (
    <Card
      title="Localizacao e cerca virtual"
      action={
        <button className="btn sm" type="button" onClick={() => void requestNow()} disabled={busy}>
          {busy ? "Solicitando..." : "Localizar agora"}
        </button>
      }
    >
      {error ? <div className="alert error">{error}</div> : null}
      {ok ? <div className="alert info">{ok}</div> : null}
      {outside ? (
        <div className="alert error" style={{ marginBottom: 8 }}>
          <strong>FORA DA CERCA VIRTUAL</strong> — {distance ? fmtDistance(distance) : "?"} da base
          {fence?.alertAt ? ` (desde ${relativeTime(fence.alertAt)})` : ""}.
        </div>
      ) : null}

      <div
        ref={mapEl}
        style={{ height: 380, borderRadius: 10, overflow: "hidden", background: "#0e1726", marginBottom: 8 }}
      />
      <p className="subtle" style={{ marginTop: 0, marginBottom: 10, fontSize: 12 }}>
        <span style={{ color: "#5b9bff" }}>●</span> posicao do computador (arraste para corrigir) ·{" "}
        <span style={{ color: "#22c55e" }}>●</span> base da cerca (arraste) ·{" "}
        <span style={{ color: "#5b9bff" }}>○</span> estimativa do Windows
      </p>

      {stale && !(fence?.enabled && fence.state === "outside") ? (
        <p className="subtle" style={{ fontSize: 12, marginTop: -4, marginBottom: 10 }}>
          Leitura do Windows sem variacao nas ultimas {stale} medicoes (mesmos roteadores de Wi-Fi). A posicao
          estimada fica fixa ate o equipamento entrar em outra rede.
        </p>
      ) : null}

      {loading ? (
        <p className="subtle"><Spinner /> carregando...</p>
      ) : last ? (
        <dl className="kv">
          <dt>Ultima atualizacao</dt>
          <dd>
            {relativeTime(last.at)}
            {pending ? <span className="badge warning" style={{ marginLeft: 8 }}>atualizando</span> : null}
          </dd>
          <dt>Raio de incerteza</dt>
          <dd>
            {last.source === "ip"
              ? "sem precisao (posicao por IP da operadora)"
              : `~${fmtAccuracy(last.accuracy)} (estimativa do Windows)`}
          </dd>
          <dt>Coordenadas</dt>
          <dd className="mono">
            {eff ? `${eff.lat.toFixed(6)}, ${eff.lon.toFixed(6)}` : "—"}
            {eff ? (
              <a
                href={`https://www.google.com/maps?q=${eff.lat},${eff.lon}`}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: 6 }}
              >
                abrir no mapa
              </a>
            ) : null}
            {manual ? (
              <div className="cell-sub">
                corrigida manualmente ({relativeTime(manual.at)})
                {canEdit ? (
                  <button className="btn sm" type="button" style={{ marginLeft: 8 }} onClick={() => void clearManual()}>
                    usar estimativa do Windows
                  </button>
                ) : null}
              </div>
            ) : null}
          </dd>
          {base ? (
            <>
              <dt>Base da cerca</dt>
              <dd>
                {base.label || "sem nome"} · raio {fmtDistance(fence?.radiusM ?? 500)}
                {distance ? <span className="cell-sub"> · distancia atual {fmtDistance(distance)}</span> : null}
              </dd>
            </>
          ) : null}
          {(() => {
            const c = manual?.city || (last.source !== "ip" ? last.city : "");
            const r = manual?.region || (last.source !== "ip" ? last.region : "");
            if (!c && !r) return null;
            return (
              <>
                <dt>Cidade</dt>
                <dd>
                  {[c, r].filter(Boolean).join(" / ")}
                  {manual?.city ? <span className="cell-sub"> (posicao corrigida)</span> : null}
                </dd>
              </>
            );
          })()}
          {manual ? (
            <>
              <dt>Leitura do Windows</dt>
              <dd className="mono">
                {last.lat.toFixed(6)}, {last.lon.toFixed(6)}
                <span className="cell-sub"> (~{fmtAccuracy(last.accuracy)})</span>
              </dd>
            </>
          ) : null}
          {last.ip ? (
            <>
              <dt>IP publico</dt>
              <dd className="mono">{last.ip}</dd>
            </>
          ) : null}
          {last.ssid ? (
            <>
              <dt>Wi-Fi</dt>
              <dd>{last.ssid}</dd>
            </>
          ) : null}
        </dl>
      ) : (
        <p className="subtle">
          Nenhuma localizacao recebida ainda. Clique em <strong>Localizar agora</strong> — o
          equipamento responde no proximo contato (ate 30 s).
        </p>
      )}

      {canEdit ? (
        <div style={{ marginTop: 10, borderTop: "1px solid var(--line, #24303f)", paddingTop: 10 }}>
          <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
            <button
              className="btn sm"
              type="button"
              onClick={() => {
                const m = mapObj.current;
                if (!m) return;
                const c = m.getCenter();
                setPendingBase({ lat: c.lat, lon: c.lng });
                markDirty();
                setOk(null);
              }}
            >
              Usar centro do mapa como base
            </button>
            <input
              className="input"
              style={{ maxWidth: 220 }}
              placeholder="Nome do local (ex.: Loja Centro)"
              value={label}
              onChange={(e) => { setLabel(e.target.value); markDirty(); }}
            />
            <label className="subtle" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Raio (m)
              <input
                className="input"
                type="number"
                min={50}
                step={50}
                style={{ width: 110 }}
                value={radius}
                onChange={(e) => { setRadius(Number(e.target.value)); markDirty(); }}
              />
            </label>
            <label className="subtle" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => { setEnabled(e.target.checked); markDirty(); }}
              />
              Cerca ativa
            </label>
            <button className="btn primary sm" type="button" onClick={() => void saveFence()} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
          <p className="subtle" style={{ marginTop: 6, fontSize: 12 }}>
            {pendingPos || pendingBase
              ? `Alteracoes pendentes: ${pendingPos ? `posicao ${pendingPos.lat.toFixed(5)}, ${pendingPos.lon.toFixed(5)}` : ""}${pendingPos && pendingBase ? " · " : ""}${pendingBase ? `base ${pendingBase.lat.toFixed(5)}, ${pendingBase.lon.toFixed(5)}` : ""} — clique em Salvar.`
              : base
                ? `Base atual: ${base.label || "sem nome"} (${base.lat.toFixed(5)}, ${base.lon.toFixed(5)})`
                : "Arraste a bolinha azul (computador) e/ou a verde (cerca) e clique em Salvar."}
          </p>
        </div>
      ) : null}

      {data && data.history.length > 1 ? (
        <details style={{ marginTop: 8 }}>
          <summary className="subtle">Historico ({data.history.length})</summary>
          <div className="table-wrap" style={{ marginTop: 6 }}>
            <table className="table">
              <thead>
                <tr><th>Quando</th><th>Raio</th><th>Fonte</th><th>Dist. base</th><th>Coordenadas</th></tr>
              </thead>
              <tbody>
                {data.history.slice(0, 12).map((h, i) => (
                  <tr key={`${h.at}-${i}`}>
                    <td className="cell-sub">{relativeTime(h.at)}</td>
                    <td className="cell-sub">~{fmtAccuracy(h.accuracy)}</td>
                    <td className="cell-sub">{sourceLabel(h.source)}</td>
                    <td className="cell-sub">{h.distance ? fmtDistance(h.distance) : "—"}</td>
                    <td className="cell-sub mono">{h.lat.toFixed(5)}, {h.lon.toFixed(5)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </Card>
  );
}
